(ns manage.slate-editor.macro-input
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/List$default" :as List]
            ["@mui/material/ListItem$default" :as ListItem]
            ["@mui/material/ListItemButton$default" :as ListItemButton]
            ["@mui/material/ListItemText$default" :as ListItemText]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Popper$default" :as Popper]
            ["react" :as react]
            [common.util :refer [spread-obj spread-props]]
            [common.widgets.input :refer [input]]
            [manage.slate-editor.codec :as codec]
            [manage.slate-editor.core :as slate-editor.core]
            [manage.slate-editor.macro-node :as macro]
            [mate.core :as mc]
            [uix.core :refer [$ defui]]))

(defui macro-input
  [{:keys [value read-only? multi-line? allow-query-params? allow-macros? sx error? placeholder data-testid
           on-change on-focus on-blur on-key-down on-key-press
           get-auto-complete-options is-macro-valid? get-popper-props]}]
  (let [container-ref (react/useRef nil)
        editor-ref (react/useRef nil)
        [suggestions set-suggestions] (react/useState {:options []
                                                       :selected-index nil})]
    ($ Box {:sx (spread-obj #js {:position "relative"} sx)
            :ref container-ref}
       (if read-only?
         ($ input
            {:disabled true
             :inputProps #js {:sx #js {:cursor "not-allowed"}
                              :data-testid data-testid}
             :defaultValue value})
         ($ :<>
            ($ slate-editor.core/slate-editor {:editor-ref    editor-ref
                                               :data-testid   data-testid
                                               :error         error?
                                               :default-value value
                                               :value         value
                                               :multiline     multi-line?
                                               :allow-query-params allow-query-params?
                                               :allow-macros  allow-macros?
                                               :placeholder   placeholder
                                               :on-change     (fn [^js editor]
                                                                ;; Update suggestion options.
                                                                (if (macro/cursor-on-inner-macro-text? editor)
                                                                  (let [macro-node (macro/get-macro-node-under-cursor editor)
                                                                        macro-content-text (macro/get-macro-content-text macro-node)
                                                                        ;; Get the entries from the dictionary which auto-completes the current text.
                                                                        options (get-auto-complete-options macro-content-text)]
                                                                    ;; Store them in the suggestion list to be displayed to the user.
                                                                    (set-suggestions {:options (vec (sort options))
                                                                                      :selected-index 0}))
                                                                  ;; Otherwise, clear the suggestion list.
                                                                  (when (seq (:options suggestions))
                                                                    (set-suggestions {:options []
                                                                                      :selected-index nil})))

                                                                ;; Invoke the callback which is only interested in the text.
                                                                (when (some? on-change)
                                                                  (let [new-value (-> editor .-children codec/slate-value->text)]
                                                                    (when (not= new-value value)
                                                                      (on-change new-value)))))
                                               :on-focus      (when (some? on-focus)
                                                                (fn [^js editor ^js _event]
                                                                  (on-focus (-> editor .-children codec/slate-value->text))))
                                               :on-blur       (fn [^js editor ^js _event]
                                                                (when (seq (:options suggestions))
                                                                  (set-suggestions {:options []
                                                                                    :selected-index nil}))
                                                                (when (some? on-blur)
                                                                  (on-blur (-> editor .-children codec/slate-value->text))))
                                               :on-key-down   (fn [^js editor ^js event]
                                                                (let [event-key (.-key event)
                                                                      ctrl-pressed (.-ctrlKey event)]
                                                                  (when (macro/cursor-on-inner-macro-text? editor)
                                                                    (cond
                                                                      ;; Select the next option in the list.
                                                                      (or (= event-key "ArrowDown")
                                                                          (and ctrl-pressed (contains? #{"s" "j"} event-key)))
                                                                      (do
                                                                        (.preventDefault event)
                                                                        (let [{:keys [options selected-index]} suggestions]
                                                                          (when (< selected-index (dec (count options)))
                                                                            (set-suggestions (update suggestions :selected-index inc)))))

                                                                      ;; Select the previous option in the list.
                                                                      (or (= event-key "ArrowUp")
                                                                          (and ctrl-pressed (contains? #{"w" "k"} event-key)))
                                                                      (do
                                                                        (.preventDefault event)
                                                                        (let [{:keys [selected-index]} suggestions]
                                                                          (when (> selected-index 0)
                                                                            (set-suggestions (update suggestions :selected-index dec)))))

                                                                      ;; Choose the selected option.
                                                                      (or (contains? #{"Tab" "Enter"} event-key)
                                                                          (and ctrl-pressed (= event-key " ")))
                                                                      (let [{:keys [options selected-index]} suggestions]
                                                                        (when-some [selected-option (get options selected-index)]
                                                                          (.preventDefault event)
                                                                          (macro/insert-macro-part editor selected-option)))))

                                                                  (when (some? on-key-down)
                                                                    (on-key-down event))))
                                               :on-key-press (fn [^js _editor ^js event]
                                                               (when (some? on-key-press)
                                                                 (on-key-press event)))
                                               :is-macro-valid? is-macro-valid?})
            ;; Render the suggestions
            (let [{:keys [options selected-index]} suggestions
                  anchor-el (.-current container-ref)]
              ($ Popper (spread-props
                         {:anchorEl anchor-el
                          :open (boolean (seq options))
                          ;; Disable the portal by default to avoid needing
                          ;; to set a custom zIndex for each `macro-input` instance.
                          :disablePortal true
                          :sx #js {:zIndex 2
                                   :width "100%"}}
                         (when get-popper-props
                           (get-popper-props {:anchorEl anchor-el})))
                 ($ Paper {:elevation 2
                           :sx #js {:maxHeight "40vh"
                                    :overflowY "auto"}}
                    ($ List
                       (for [[option-index option-text] (mc/seq-indexed options)]
                         ($ ListItem {:key option-text
                                      :disablePadding true
                                      :dense true}
                            ($ ListItemButton {:selected (= option-index selected-index)
                                               :on-mouse-down (fn [^js event]
                                                                (.preventDefault event) ; avoids blurring the Slate component
                                                                (when-some [editor (.-current editor-ref)]
                                                                  (macro/insert-macro-part editor option-text)))}
                               ($ ListItemText {:primary option-text})))))))))))))
